/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DynamicRequestForm from '../shared/requests/DynamicRequestForm';
import RequestHistoryTimeline from '../shared/requests/RequestHistoryTimeline';
import CartRequestAssignmentsPanel from '../shared/requests/CartRequestAssignmentsPanel';
import RequestThread from '../shared/requests/RequestThread';
import RequestChangeLog from '../shared/requests/RequestChangeLog';
import {
  describeDays,
  formatDuration,
  isEmptyScheduleEntry
} from '../../utils/waterSchedule';
import {
  REQUEST_STATUSES,
  normalizeRequestStatus,
  requestStatusClass,
  requestStatusLabel
} from '../../utils/requestStatus';
import '../../styles/RequestReview.css';
import { formatDateTime, formatClock } from '../../utils/timeFormat';

// What each manual move does, and the copy for its confirmation dialog. There
// is deliberately no automatic move: a request leaves New only because an
// admin said so, so opening one by mistake can never claim it from under the
// other admins.
const STATUS_MOVES = {
  New: {
    label: 'Move back to New',
    btnClass: 'req-action-btn status-new',
    ctaColor: 'inherit',
    requireComment: false,
    title: 'Move back to New',
    hint: 'Puts the request back in the New tab so another admin can pick it up. Optional note for the project team.'
  },
  Pending: {
    label: 'Mark as Pending',
    btnClass: 'req-action-btn status-pending',
    ctaColor: 'warning',
    requireComment: false,
    title: 'Mark as Pending',
    hint: 'Says you have taken this on. Optional note for the project team — use the request thread for the back and forth.'
  },
  Completed: {
    label: 'Mark as Completed',
    btnClass: 'req-action-btn status-completed',
    ctaColor: 'success',
    requireComment: false,
    title: 'Mark as Completed',
    hint: 'The work asked for is done. Optional note for the project team. Completed requests still allow follow-up actions such as compartment assignment edits.'
  },
  Rejected: {
    label: 'Reject',
    btnClass: 'req-action-btn reject',
    ctaColor: 'error',
    requireComment: true,
    title: 'Reject Request',
    hint: 'Give a reason. The project team is notified and can read it on the request.'
  }
};

/* ────────────────────────────────────────────────────────────
   Project details popup — shows ALL major project info
   ──────────────────────────────────────────────────────────── */
const ProjectDetailsDialog = ({ projectId, open, onClose }) => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`/api/projects/${projectId}`)
      .then(({ data }) => { if (!cancelled) setProject(data); })
      .catch(err => {
        console.error('Project load error:', err);
        if (!cancelled) setError('Failed to load project');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, open]);

  const fmt = (v) => (v === null || v === undefined || v === '' ? <span className="req-pdetail-empty">—</span> : v);
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : <span className="req-pdetail-empty">—</span>;
  const fmtBool = (v) => (v ? 'Yes' : 'No');

  const renderUsers = () => {
    if (!project?.users?.length) return <span className="req-pdetail-empty">No users</span>;
    return (
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Department</th>
          </tr>
        </thead>
        <tbody>
          {project.users.filter(u => !u.isDeleted).map((u, i) => {
            const name = `${u.first_name || u.user?.first_name || ''} ${u.last_name || u.user?.last_name || ''}`.trim();
            const dept = (typeof u.department === 'object' ? u.department?.department_name : null) || u.customDepartment || '—';
            const role = typeof u.role === 'object' ? (u.role?.role || u.role?.name) : u.role;
            return (
              <tr key={i}>
                <td>{name || '—'}</td>
                <td>{u.email || u.user?.email || '—'}</td>
                <td>{role || '—'}</td>
                <td>{dept}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderConditions = () => {
    if (!project?.experiment_conditions?.length) return <span className="req-pdetail-empty">No conditions</span>;
    return (
      <table>
        <thead>
          <tr>
            <th>Photo On</th><th>Photo Off</th>
            <th>NI On</th><th>NI Off</th>
            <th>Temp Day</th><th>Temp Night</th>
            <th>CO₂ Day</th><th>CO₂ Night</th>
            <th>Hum Day</th><th>Hum Night</th>
          </tr>
        </thead>
        <tbody>
          {project.experiment_conditions.map((c, i) => (
            <tr key={i}>
              <td>{c.photo_on ? formatClock(c.photo_on) : '—'}</td>
              <td>{c.photo_off ? formatClock(c.photo_off) : '—'}</td>
              <td>{c.night_interrupt_on ? formatClock(c.night_interrupt_on) : '—'}</td>
              <td>{c.night_interrupt_off ? formatClock(c.night_interrupt_off) : '—'}</td>
              <td>{c.temp_day || '—'}</td>
              <td>{c.temp_night || '—'}</td>
              <td>{c.co2_day || '—'}</td>
              <td>{c.co2_night || '—'}</td>
              <td>{c.humidity_day || '—'}</td>
              <td>{c.humidity_night || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderDetails = () => {
    if (!project?.experiment_details?.length) return <span className="req-pdetail-empty">No details</span>;
    return (
      <table>
        <thead>
          <tr>
            <th>Water</th>
            <th>Substrate</th>
            <th>Species</th>
          </tr>
        </thead>
        <tbody>
          {project.experiment_details.map((d, i) => (
            <tr key={i}>
              <td>{d.water?.water || d.water?.name || '—'}</td>
              <td>{d.substrate?.substrate || d.substrate?.name || '—'}</td>
              <td>{d.species?.species || d.species?.name || d.species_not_listed || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderFunding = () => {
    if (!project?.funding?.length) return <span className="req-pdetail-empty">No funding info</span>;
    return (
      <table>
        <thead>
          <tr>
            <th>User</th>
            <th>Account #</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {project.funding.map((f, i) => (
            <tr key={i}>
              <td>{f.user_email || '—'}</td>
              <td>{f.account_number || '—'}</td>
              <td>{f.source_of_funds || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Project Details
        {project?.project_id ? ` · ${project.project_id}` : ''}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ background: '#f8fafc' }}>
        {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {project && (
          <>
            <div className="req-pdetail-section">
              <h6>Overview</h6>
              <div className="req-pdetail-grid">
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Proposal ID</span>
                  <span className="req-pdetail-val">{fmt(project.project_id)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Status</span>
                  <span className="req-pdetail-val">{fmt(project.project_status)}</span>
                </div>
                <div className="req-pdetail-row" style={{ gridColumn: '1 / -1' }}>
                  <span className="req-pdetail-key">Project Title</span>
                  <span className="req-pdetail-val">{fmt(project.project_title)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Facility</span>
                  <span className="req-pdetail-val">{fmt(project.facility?.facility_name)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Application Type</span>
                  <span className="req-pdetail-val">{fmt(project.application_type)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Start Date</span>
                  <span className="req-pdetail-val">{fmtDate(project.project_start_date)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">End Date</span>
                  <span className="req-pdetail-val">{fmtDate(project.project_end_date)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">BUA Number</span>
                  <span className="req-pdetail-val">{fmt(project.bua_number)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Last Invoice Date</span>
                  <span className="req-pdetail-val">{fmtDate(project.last_invoice_date)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Uses Transgenic Plants</span>
                  <span className="req-pdetail-val">{fmtBool(project.uses_transgenic_plants)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Uses Pathogen</span>
                  <span className="req-pdetail-val">{fmtBool(project.uses_pathogen)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Safety Training Verified</span>
                  <span className="req-pdetail-val">{fmtBool(project.safety_training_verified)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Preferred Space</span>
                  <span className="req-pdetail-val">{fmt(project.preferred_space)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Number of Carts</span>
                  <span className="req-pdetail-val">{fmt(project.number_of_carts)}</span>
                </div>
                <div className="req-pdetail-row">
                  <span className="req-pdetail-key">Renewal Count</span>
                  <span className="req-pdetail-val">{fmt(project.renewal_count)}</span>
                </div>
              </div>
            </div>

            <div className="req-pdetail-section">
              <h6>Team / Personnel</h6>
              {renderUsers()}
            </div>

            {project.experimental_design_summary && (
              <div className="req-pdetail-section">
                <h6>Experimental Design Summary</h6>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {project.experimental_design_summary}
                </Typography>
              </div>
            )}

            {project.project_timeline && (
              <div className="req-pdetail-section">
                <h6>Project Timeline</h6>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {project.project_timeline}
                </Typography>
              </div>
            )}

            {project.special_environmental_requirements && (
              <div className="req-pdetail-section">
                <h6>Special Environmental Requirements</h6>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {project.special_environmental_requirements}
                </Typography>
              </div>
            )}

            {project.additional_comments && (
              <div className="req-pdetail-section">
                <h6>Additional Comments</h6>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {project.additional_comments}
                </Typography>
              </div>
            )}

            <div className="req-pdetail-section">
              <h6>Experiment Conditions</h6>
              {renderConditions()}
            </div>

            <div className="req-pdetail-section">
              <h6>Experiment Details</h6>
              {renderDetails()}
            </div>

            <div className="req-pdetail-section">
              <h6>Funding</h6>
              {renderFunding()}
            </div>

            {project.resource_request_purpose && (
              <div className="req-pdetail-section">
                <h6>Resource Request Purpose</h6>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {project.resource_request_purpose}
                </Typography>
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ────────────────────────────────────────────────────────────
   Action dialog — collects comment + attachments for status change
   ──────────────────────────────────────────────────────────── */
const ActionDialog = ({ open, onClose, onSubmit, title, ctaLabel, ctaColor, requireComment = true, hint }) => {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setComment(''); }
  }, [open]);

  const handleSubmit = async () => {
    if (requireComment && !comment.trim()) {
      toast.error('Please add a comment');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ comment });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {hint && <Alert severity="info" sx={{ mb: 2 }}>{hint}</Alert>}
        <TextField
          fullWidth
          multiline
          rows={4}
          label={`Comment${requireComment ? ' *' : ''}`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color={ctaColor || 'primary'}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : ctaLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ────────────────────────────────────────────────────────────
   Request attachments — admin upload (max 5) / delete / download
   ──────────────────────────────────────────────────────────── */
const downloadRequestAttachment = async (requestId, attachment) => {
  try {
    const res = await axios.get(
      `/api/requests/${requestId}/attachments/${attachment._id}`,
      { responseType: 'arraybuffer' }
    );
    const blob = new Blob([res.data], { type: res.headers['content-type'] });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = attachment.filename || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error('Error downloading attachment:', err);
    toast.error('Failed to download attachment');
  }
};

const RequestAttachmentsCard = ({ request, onChanged }) => {
  const [uploading, setUploading] = useState(false);
  const attachments = request.attachments || [];
  const remaining = 5 - attachments.length;

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (files.length > remaining) {
      toast.error(`You can attach ${remaining} more file${remaining !== 1 ? 's' : ''} (max 5).`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('attachments', f));
      await axios.post(`/api/admin/requests/${request._id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Attachment(s) uploaded');
      onChanged();
    } catch (err) {
      console.error('Upload error:', err);
      const data = err.response?.data?.error;
      const message = typeof data === 'string'
        ? data
        : (data?.message || err.response?.data?.message || 'Failed to upload');
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    try {
      await axios.delete(`/api/admin/requests/${request._id}/attachments/${attachmentId}`);
      toast.success('Attachment removed');
      onChanged();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to remove attachment');
    }
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Files you attach here are visible to the client. Up to 5 files of any type (10 MB each).
      </Typography>
      {attachments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No files attached yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 1 }}>
          {attachments.map((att) => (
            <Stack key={att._id} direction="row" alignItems="center" spacing={1}>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => downloadRequestAttachment(request._id, att)}
                sx={{ textTransform: 'none', flex: 1, justifyContent: 'flex-start' }}
              >
                {att.filename}
              </Button>
              <IconButton aria-label="Delete" size="small" color="error" onClick={() => handleDelete(att._id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
      <Button
        variant="outlined"
        size="small"
        component="label"
        startIcon={<AttachFileIcon />}
        disabled={uploading || remaining <= 0}
      >
        {uploading ? 'Uploading…' : remaining > 0 ? `Attach files (${remaining} left)` : 'Limit reached'}
        <input type="file" hidden multiple onChange={handleUpload} />
      </Button>
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────
   Requested watering schedule — apply it to the project
   ──────────────────────────────────────────────────────────── */
const RequestedWateringCard = ({ request, onChanged }) => {
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const entries = (request.form_data?.waterSchedules || []).filter(e => !isEmptyScheduleEntry(e));
  const projectId = typeof request.project_id === 'object' ? request.project_id?._id : request.project_id;

  const handleApply = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(
        `/api/projects/${projectId}/environment-schedules/apply-request/${request._id}`,
        { start_date: startDate }
      );
      toast.success(`${data.applied} watering schedule${data.applied === 1 ? '' : 's'} now in effect`);
      setConfirmOpen(false);
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Apply schedule error:', err);
      toast.error(err.response?.data?.error || 'Failed to apply the schedule');
    } finally {
      setApplying(false);
    }
  };

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This request does not ask for a watering change.
      </Typography>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        Applying this records it against the project from the date you choose. Whatever that
        compartment and water is on now is ended the day before, so the previous schedule keeps its
        dates. You can adjust it afterwards on the project's review page.
      </Alert>

      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        {entries.map((entry, i) => (
          <Box key={entry.water || i} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {entry.water_label || 'Water'}
            </Typography>
            <Typography variant="body2">{describeDays(entry.week1, entry.week2)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {entry.times_per_day ? `${entry.times_per_day}×/day` : 'Frequency not given'}
              {' · '}{formatDuration(entry.duration)}
            </Typography>
            {entry.comments && (
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{entry.comments}</Typography>
            )}
          </Box>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          type="date"
          size="small"
          label="In effect from"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={!startDate}>
          Apply to project
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={applying ? undefined : () => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply this watering schedule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {entries.length} schedule{entries.length === 1 ? '' : 's'} will be recorded against{' '}
            <strong>{request.project_title}</strong>, in effect from{' '}
            <strong>{new Date(`${startDate}T00:00:00`).toLocaleDateString()}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            The project team is notified. This does not change the request's status — mark it
            Completed yourself once the work is actually done.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>Cancel</Button>
          <Button variant="contained" onClick={handleApply} disabled={applying}>
            {applying ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────
   End Project panel — for Project Termination requests
   ──────────────────────────────────────────────────────────── */
const TerminateProjectCard = ({ request, onChanged }) => {
  const [endDate, setEndDate] = useState(request.form_data?.terminationDate || '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!endDate) {
      toast.error('Please select an end date');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`/api/admin/requests/${request._id}/terminate-project`, {
        end_date: endDate
      });
      toast.success(data.message || 'Project termination processed');
      setConfirmOpen(false);
      onChanged();
    } catch (err) {
      console.error('Terminate error:', err);
      toast.error(err.response?.data?.error || 'Failed to end project');
    } finally {
      setSubmitting(false);
    }
  };

  const isFuture = endDate && new Date(`${endDate}T23:59:59`).getTime() > Date.now();

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        Ending the project sets its end date and frees the compartment space. Active assignments end on
        this date (inclusive); past assignments keep their original end dates. A future date keeps the
        project active until that day.
      </Alert>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          type="date"
          size="small"
          label="Project End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <Button
          variant="contained"
          color="error"
          onClick={() => setConfirmOpen(true)}
          disabled={!endDate}
        >
          End Project
        </Button>
      </Stack>

      <Dialog open={confirmOpen} onClose={submitting ? undefined : () => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>End this project?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The project <strong>{request.project_title}</strong> will be set to end on{' '}
            <strong>{endDate ? new Date(`${endDate}T00:00:00`).toLocaleDateString() : '—'}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {isFuture
              ? 'The project stays active until that date; the space is freed automatically on the end date.'
              : 'The space will be freed immediately. The project will be marked Completed.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Processing…' : 'Confirm End Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────
   Main page
   ──────────────────────────────────────────────────────────── */
const RequestReview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  // Which status move is being confirmed: null | 'New' | 'Pending' | 'Completed' | 'Rejected'
  const [statusDialog, setStatusDialog] = useState(null);

  // Right pane: collapsible cards + resizable divider
  const [assignmentsExpanded, setAssignmentsExpanded] = useState(false);
  const [terminationExpanded, setTerminationExpanded] = useState(true);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);
  const [commentsExpanded, setCommentsExpanded] = useState(true);
  const [changesExpanded, setChangesExpanded] = useState(false);
  const [wateringExpanded, setWateringExpanded] = useState(true);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [assignmentsPercent, setAssignmentsPercent] = useState(45);
  const rightPaneRef = useRef(null);
  const isDraggingRef = useRef(false);

  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const move = (ev) => {
      if (!isDraggingRef.current || !rightPaneRef.current) return;
      const rect = rightPaneRef.current.getBoundingClientRect();
      const offsetY = ev.clientY - rect.top;
      const percent = Math.min(80, Math.max(15, (offsetY / rect.height) * 100));
      setAssignmentsPercent(percent);
    };
    const up = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`/api/requests/${id}`);
      setRequest(data);
    } catch (err) {
      console.error('Error loading request:', err);
      setError('Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const performStatusChange = async (status, { comment }) => {
    try {
      await axios.patch(`/api/admin/requests/${id}/status`, {
        status,
        comment: comment || ''
      });
      toast.success(`Request moved to ${status}`);
      load();
    } catch (err) {
      console.error('Status change error:', err);
      toast.error(err.response?.data?.error || 'Failed to update status');
      throw err;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !request) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error || 'Request not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/admin/requests')} startIcon={<ArrowBackIcon />}>
          Back to Requests
        </Button>
      </Box>
    );
  }

  const projectId = typeof request.project_id === 'object' ? request.project_id?._id : request.project_id;
  const isCart = request.request_type_key === 'cart_request';
  const isTermination = request.request_type_key === 'project_termination';
  const requestedWatering = (request.form_data?.waterSchedules || [])
    .filter(e => !isEmptyScheduleEntry(e));
  // Legacy requests may still carry 'Approved' / 'Revision Required'; show and
  // act on the tab they belong to.
  const status = normalizeRequestStatus(request.status);
  const lastMove = (request.status_history || []).slice(-1)[0];
  const lastMoveBy = lastMove
    ? (lastMove.changed_by
      ? `${lastMove.changed_by.first_name || ''} ${lastMove.changed_by.last_name || ''}`.trim()
        || lastMove.changed_by.email
      : lastMove.changed_by_name)
    : null;
  // Every status other than the current one is one click away, so an admin can
  // correct a wrong move as easily as making one.
  const availableMoves = REQUEST_STATUSES.filter(st => st !== status);

  return (
    <Box className="request-review">
      <div className="req-review-two-pane">
        {/* ─── Left Pane: form & actions ─── */}
        <div className="req-review-left-pane">
          <div className="req-section-card">
            <div className="req-section-header">
              <span className="req-section-title">
                {request.request_type}
                <span style={{ opacity: 0.7, fontWeight: 400, marginLeft: 8 }}>
                  · {request.request_id}
                  {request.project_title ? ` · ${request.project_title}` : ''}
                  {request.facility?.facility_name ? ` · ${request.facility.facility_name}` : ''}
                </span>
              </span>
              <span className={`req-status-pill ${requestStatusClass(status)}`}>
                {requestStatusLabel(status)}
              </span>
            </div>

            <div className="req-section-body">
              <div className="req-action-row">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate('/admin/requests')}
                >
                  Back
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className="req-action-btn view-project"
                  startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setProjectDialogOpen(true)}
                >
                  View Project Details
                </Button>
                {availableMoves.map(moveTo => (
                  <Button
                    key={moveTo}
                    size="small"
                    variant="contained"
                    className={STATUS_MOVES[moveTo].btnClass}
                    onClick={() => setStatusDialog(moveTo)}
                  >
                    {STATUS_MOVES[moveTo].label}
                  </Button>
                ))}
              </div>

              {lastMoveBy && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mx: 2, mt: 1 }}>
                  Moved to {requestStatusLabel(lastMove.to_status)} by {lastMoveBy}
                  {lastMove.changed_at ? ` on ${formatDateTime(lastMove.changed_at)}` : ''}
                  {lastMove.from_status ? ` (from ${requestStatusLabel(lastMove.from_status)})` : ''}
                </Typography>
              )}

              <Alert severity="info" sx={{ mx: 2, mt: 1 }}>
                This is the request form submitted by the client. You may edit it below — every
                change is logged in "Form Changes". Statuses never move on their own: opening a
                request leaves it where it is, so use the buttons above when you take it on.
                To talk to the project team, use the request thread on the right.
              </Alert>

              <div className="req-form-scroll">
                <DynamicRequestForm
                  key={request.updatedAt || request._id}
                  request={request}
                  mode="admin_edit"
                  onSubmitted={() => load()}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Right Pane ─── */}
        <div className="req-review-right-pane" ref={rightPaneRef}>
          {isCart && (
            <>
              <div
                className={`req-section-card req-card-assignments ${assignmentsExpanded ? 'expanded' : 'collapsed'}`}
                style={assignmentsExpanded ? { flex: `0 0 ${assignmentsPercent}%` } : undefined}
              >
                <div className="req-section-header clickable" onClick={() => setAssignmentsExpanded(v => !v)}>
                  <span className="req-section-title">Compartment Assignments</span>
                  <IconButton aria-label="Expand or collapse" size="small">
                    {assignmentsExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                </div>
                {assignmentsExpanded && (
                  <div className="req-card-body">
                    <CartRequestAssignmentsPanel request={request} onChanged={() => load()} />
                  </div>
                )}
              </div>

              {assignmentsExpanded && (
                <div className="req-pane-divider" onMouseDown={handleDividerMouseDown}>
                  <div className="req-pane-divider-handle" />
                </div>
              )}
            </>
          )}

          {isTermination && (
            <div className="req-section-card">
              <div className="req-section-header clickable" onClick={() => setTerminationExpanded(v => !v)}>
                <span className="req-section-title">End Project</span>
                <IconButton aria-label="Expand or collapse" size="small">
                  {terminationExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
              </div>
              {terminationExpanded && (
                <div className="req-card-body">
                  <TerminateProjectCard request={request} onChanged={() => load()} />
                </div>
              )}
            </div>
          )}

          {requestedWatering.length > 0 && (
            <div className="req-section-card">
              <div className="req-section-header clickable" onClick={() => setWateringExpanded(v => !v)}>
                <span className="req-section-title">
                  Watering Schedule Requested ({requestedWatering.length})
                </span>
                <IconButton aria-label="Expand or collapse" size="small">
                  {wateringExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
              </div>
              {wateringExpanded && (
                <div className="req-card-body">
                  <RequestedWateringCard request={request} onChanged={() => load()} />
                </div>
              )}
            </div>
          )}

          <div className="req-section-card">
            <div className="req-section-header clickable" onClick={() => setAttachmentsExpanded(v => !v)}>
              <span className="req-section-title">Request Attachments</span>
              <IconButton aria-label="Expand or collapse" size="small">
                {attachmentsExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </div>
            {attachmentsExpanded && (
              <div className="req-card-body">
                <RequestAttachmentsCard request={request} onChanged={() => load()} />
              </div>
            )}
          </div>

          <div className="req-section-card">
            <div className="req-section-header clickable" onClick={() => setCommentsExpanded(v => !v)}>
              <span className="req-section-title">Request Thread</span>
              <IconButton aria-label="Expand or collapse" size="small">
                {commentsExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </div>
            {commentsExpanded && (
              <div className="req-card-body">
                <RequestThread request={request} isAdmin onChanged={() => load()} />
              </div>
            )}
          </div>

          <div className="req-section-card">
            <div className="req-section-header clickable" onClick={() => setChangesExpanded(v => !v)}>
              <span className="req-section-title">
                Form Changes ({(request.form_changes || []).length})
              </span>
              <IconButton aria-label="Expand or collapse" size="small">
                {changesExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </div>
            {changesExpanded && (
              <div className="req-card-body">
                <RequestChangeLog request={request} />
              </div>
            )}
          </div>

          <div className={`req-section-card req-card-activity ${activityExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="req-section-header clickable" onClick={() => setActivityExpanded(v => !v)}>
              <span className="req-section-title">Activity & History</span>
              <IconButton aria-label="Expand or collapse" size="small">
                {activityExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </div>
            {activityExpanded && (
              <div className="req-card-body">
                <RequestHistoryTimeline request={request} isAdmin />
              </div>
            )}
          </div>
        </div>
      </div>

      <ProjectDetailsDialog
        projectId={projectId}
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
      />

      {REQUEST_STATUSES.map(moveTo => (
        <ActionDialog
          key={moveTo}
          open={statusDialog === moveTo}
          onClose={() => setStatusDialog(null)}
          onSubmit={(data) => performStatusChange(moveTo, data)}
          title={STATUS_MOVES[moveTo].title}
          ctaLabel={STATUS_MOVES[moveTo].label}
          ctaColor={STATUS_MOVES[moveTo].ctaColor}
          requireComment={STATUS_MOVES[moveTo].requireComment}
          hint={STATUS_MOVES[moveTo].hint}
        />
      ))}
    </Box>
  );
};

export default RequestReview;
